import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { SearchBox } from "../../../components/Search";
import { stringToConsistentColor } from "../../../extensions/string";
import { BrushStart, ChartType } from "../../../models/chart_model";
import { RajasthanDiscomData } from "../../../models/rajasthan";
import {
  fetchRvpnData,
  getRajasthanDiscomData,
} from "../../../Rest_api/DemoRestApi";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { formatDateForAPI, R_CHART_TYPES } from "../const";
import { DEMAND_UNITS } from "./Data";
import { getMonthFromDate } from "../../../extensions/date";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import { useDispatch, useSelector } from "react-redux";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { RajasthanSummaryAnalysis } from "./DiscomSummary";
import { RajasthanPeakAnalysis } from "./DiscomPeak";
// ▶ NEW — shared hook
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip from "../../../components/Metrics";
import { getDiscomLoadMetrics } from "../../Rajasthan/Utility/RestApi";
import { DiscomLoadMetrics } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";

// ─── constants ───────────────────────────────────────────────────────────────
const GROUP_BY = ["Discom Load", "Total Discom Load", "RVPN Drawal"];
const DISCOM = [
  { label: "Discom 1", key: "Discom 1" },
  { label: "Discom 2", key: "Discom 2" },
  { label: "Discom 3", key: "Discom 3" },
];

let Intervals = [
  { label: "Time Slot", key: "time", values: ["block", "date"], isTime: true },
  { label: "Date", key: "date", values: ["date"], isTime: false },
  { label: "Month", key: "month", values: ["month"], isTime: false },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const TYPE = ["Schedule", "Drawal", "Load Shedding", "Total Load"];

// ─── component ────────────────────────────────────────────────────────────────
export function RajasthanDiscomAnalysis() {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);
  const [selectedUnit, setSelectedUnit] = useState(DEMAND_UNITS[0]);
  const [data, setData] = useState<RajasthanDiscomData[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[0]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<DiscomLoadMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const dispatch = useDispatch();

  // ─── suffix config ───────────────────────────────────────────────────────
  const chartSuffixes = useMemo(
    () =>
      selectedGroupKeys === "RVPN Drawal"
        ? [
            {
              suffix: "rvpnl_drawal",
              displayName: "RVPN Drawal",
              stackSuffix: "rvpnl_drawal",
            },
          ]
        : [
            {
              suffix: "schedule",
              displayName: "Schedule",
              stackSuffix: "schedule",
            },
            { suffix: "drawal", displayName: "Drawal", stackSuffix: "drawal" },
            {
              suffix: "load_shedding",
              displayName: "Load Shedding",
              stackSuffix: "load_shedding",
            },
            {
              suffix: "total",
              displayName: "Total Load",
              stackSuffix: "total",
            },
          ],
    [selectedGroupKeys],
  );

  const [selectedSuffix, setSelectedSuffix] = useState([
    chartSuffixes[0],
    chartSuffixes[1],
  ]);

  useEffect(() => {
    if (selectedGroupKeys === "RVPN Drawal") {
      setSelectedSuffix([
        {
          suffix: "rvpnl_drawal",
          displayName: "RVPN Drawal",
          stackSuffix: "rvpnl_drawal",
        },
      ]);
    } else {
      setSelectedSuffix([chartSuffixes[0], chartSuffixes[1]]);
    }
  }, [chartSuffixes, selectedGroupKeys]);

  // ▶ UPDATED — safe color indexing with modulo (from original)
  const createChartObject = useCallback(
    (
      key: string,
      index: number,
      suffix: string,
      displayName: string,
      stackSuffix: string,
      selectedInterval: { isTime: boolean },
      selectedChartType: { value: ChartType; stackId?: string },
      ExchangeColors: string[],
    ) => ({
      dataKey: `${key}_${suffix}_${selectedUnit.toLowerCase()}`,
      name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`,
      stroke:
        suffix === "schedule"
          ? lightenColor(ExchangeColors[index % ExchangeColors.length], 25)
          : suffix === "drawal"
            ? ExchangeColors[index % ExchangeColors.length]
            : stringToConsistentColor(`${key}_${suffix}`),
      type: selectedChartType.value as ChartType,
      stackId:
        selectedChartType.stackId !== undefined ? stackSuffix : undefined,
    }),
    [selectedUnit],
  );

  // `legends` = the FULL list the chart always needs
  const legends = useMemo(() => {
    return selectedSuffix.flatMap(({ suffix, displayName, stackSuffix }) =>
      chartKeys.map((key, index) =>
        createChartObject(
          key,
          index,
          suffix,
          displayName,
          stackSuffix,
          selectedInterval,
          selectedChartType,
          ExchangeColors,
        ),
      ),
    );
  }, [
    selectedSuffix,
    chartKeys,
    selectedInterval,
    selectedChartType,
    selectedUnit,
    createChartObject,
  ]);

  // ▶ UPDATED — Promise.all fetch pattern (from original), demo API imports kept
  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab]?.defaultRangeDate === undefined) return;
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const startDateStr = formatDateForAPI(dates.startDate);
    const endDateStr = formatDateForAPI(dates.endDate);

    const [newData, rvpnData] = await Promise.all([
      getRajasthanDiscomData(startDateStr, endDateStr),
      fetchRvpnData(startDateStr, endDateStr),
    ]);

    let transformedBewData = newData.map((item) => ({
      ...item,
      total_mw: (item.load_shedding_mw || 0) + (item.drawal_mw || 0),
    }));

    let transformedRvpnData = (rvpnData || []).map((item: any) => {
      const { drawal_mw, ...rest } = item;
      return {
        ...rest,
        rvpnl_drawal_mwh: (item.drawal_mw || 0) / 4,
        rvpnl_drawal_mw: item.drawal_mw || 0,
        rvpnl_drawal_lu: (item.drawal_mw || 0) / 400,
        block: rest.slot_id,
      };
    });

    setData(
      selectedGroupKeys === "RVPN Drawal"
        ? [...transformedRvpnData]
        : [...transformedBewData],
    );

    setUniqueDate(
      Array.from(
        new Set(
          newData
            .map((item) => item.date)
            .filter(Boolean)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
        ),
      ),
    );
    setUniqueBlock(
      Array.from(new Set(newData.map((item) => item.block).filter(Boolean))),
    );
    setLoading(false);
  }, [DefaultDates, activeTab, selectedGroupKeys]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const fetchMetrics = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultRangeDate) {
      setMetrics(null);
      return;
    }

    setMetricsLoading(true);
    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const metricsData = await getDiscomLoadMetrics({
        ranges: [
          {
            startDate: startDateStr,
            endDate: endDateStr,
            blocks: selectedBlock.map((block: any) => block.value),
          },
        ],
        aggregated: true,
      });

      setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [DefaultDates, activeTab, selectedBlock]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // ─── handlers ────────────────────────────────────────────────────────────
  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys(GROUP_BY[index]);
    setSelectedDiscom([]);
    setSelectedArea([]);
    setSelectedDate([]);
  }

  function handleXAxisModeChange(item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  function handleUnitChange(params: string) {
    setSelectedUnit(params);
  }

  function handleDiscomChange(items: string[]) {
    setSelectedDiscom(items);
  }

  function handleTypeChange(selected: string[]) {
    if (selectedGroupKeys === "RVPN Drawal") {
      dispatch(setLoadingFilterName(""));
      return;
    }
    setSelectedSuffix(
      chartSuffixes.filter((suffixObj) =>
        selected.includes(suffixObj.displayName),
      ),
    );
  }

  // ▶ UPDATED — bug fix: filter from filteredData (not raw data) for discom (from original)
  useEffect(() => {
    let filteredData = data.map((item) => {
      const dateParts = (item.date || "").split("-");
      let monthStr = "";
      if (dateParts.length === 3) {
        const year = dateParts[0];
        const monthIndex = parseInt(dateParts[1]) - 1;
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        monthStr =
          monthIndex >= 0 && monthIndex < 12
            ? `${monthNames[monthIndex]}-${year.slice(-2)}`
            : getMonthFromDate(item.date);
      } else {
        monthStr = getMonthFromDate(item.date);
      }
      return { ...item, month: monthStr };
    });

    if (selectedGroupKeys !== "RVPN Drawal" && selectedDiscom.length > 0)
      filteredData = filteredData.filter((item) =>
        selectedDiscom.includes(item.discom),
      );

    if (selectedDate.length > 0)
      filteredData = filteredData.filter((item) =>
        selectedDate.map((d) => d.value).includes(item.date),
      );

    if (selectedBlock.length > 0)
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((b) => b.value).includes(item.block),
      );

    function getKeys() {
      if (selectedInterval.key === "time") return ["date", "block"];
      if (selectedInterval.key === "date") return ["date"];
      return [selectedInterval.values[0]];
    }

    const par: ChartDataParams = {
      keys: getKeys(),
      tableKeys: ["date", "block", "month"],
      groupBy: [selectedGroupKeys.toLowerCase()].concat(
        selectedGroupKeys === "Discom Load" ? ["discom"] : [],
      ),
      volumeKey: "",
      aggregationKeys:
        selectedGroupKeys === "RVPN Drawal"
          ? ["rvpnl_drawal_mw", "rvpnl_drawal_mwh", "rvpnl_drawal_lu"]
          : ["schedule", "drawal", "load_shedding", "total"].map(
              (item) => item + "_" + selectedUnit.toLowerCase(),
            ),
      averageKeys:
        selectedGroupKeys === "RVPN Drawal"
          ? ["rvpnl_drawal_mw"]
          : ["schedule_mw", "drawal_mw", "load_shedding_mw", "total_mw"],
      averageDuplicationKeys: ["date", "block"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [
    data,
    selectedGroupKeys,
    selectedInterval,
    selectedDiscom,
    selectedArea,
    selectedDate,
    selectedBlock,
    selectedSuffix,
    selectedUnit,
  ]);

  // ─── x-axis helpers ──────────────────────────────────────────────────────
  const handlexDataKey = useCallback(() => {
    if (selectedInterval.key === "time") return "block";
    return selectedInterval.key;
  }, [selectedBlock, selectedInterval]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  function secondDataKey() {
    return selectedInterval.isTime ? "date" : undefined;
  }

  // ─── table params ─────────────────────────────────────────────────────────
  const summaryExtraKeys = selectedInterval.isTime
    ? ["block", selectedInterval.values[1]]
    : [selectedInterval.values[0]];

  const summaryExtraLabels = selectedInterval.values.map((value) =>
    value === "date" ? "Date" : value,
  );

  // ▶ legend filter hook
  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Discom Load Demo",
      dates: DefaultDates[activeTab]?.defaultRangeDate,
    }),
  };

  // ─── sub-page routes ──────────────────────────────────────────────────────
  if (selectedXAxisMode.value === "peak") {
    return (
      <RajasthanPeakAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        selectedDiscom={selectedDiscom}
        onDiscomChange={handleDiscomChange}
        selectedGroupKeys={selectedGroupKeys}
        onGroupByChange={_handleGroupByChange}
        selectedSuffix={selectedSuffix}
        onTypeChange={handleTypeChange}
        chartSuffixes={chartSuffixes}
        metrics={metrics}
      />
    );
  }

  if (selectedXAxisMode.value === "summary") {
    return (
      <RajasthanSummaryAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        selectedDiscom={selectedDiscom}
        onDiscomChange={handleDiscomChange}
        selectedGroupKeys={selectedGroupKeys}
        onGroupByChange={_handleGroupByChange}
        selectedSuffix={selectedSuffix}
        onTypeChange={handleTypeChange}
        chartSuffixes={chartSuffixes}
        metrics={metrics}
      />
    );
  }

  // ─── render ───────────────────────────────────────────────────────────────
  return (
    <>
      <div className="gas-page-container">
        <NewFilterContainer>
          <div className="side-filters-container text-nowrap">
            <FilterMenu
              title="X-Axis"
              items={X_AXIS_MODES.map((item) => item.label)}
              selectedItems={[selectedXAxisMode.label]}
              onSelect={handleXAxisModeChange}
            />
            <FilterMenu
              title="Intervals"
              items={Intervals.map((item) => item.label)}
              selectedItems={[selectedInterval.label]}
              onSelect={(item, index) => setSelectedInterval(Intervals[index])}
            />
            {selectedGroupKeys !== "RVPN Drawal" && (
              <FilterMenu
                title="Discom"
                items={DISCOM.map((item) => item.label)}
                selectedItems={selectedDiscom}
                onSelect={() => {}}
                setSelectedItems={(item) => {
                  setSelectedDiscom(item);
                }}
              />
            )}
            <FilterMenu
              title="Unit"
              items={DEMAND_UNITS}
              selectedItems={[selectedUnit]}
              onSelect={(item) => handleUnitChange(item)}
            />
            <FilterMenu
              title="Group By"
              items={GROUP_BY}
              selectedItems={[selectedGroupKeys]}
              onSelect={(item, index) => _handleGroupByChange(index)}
            />
            {selectedGroupKeys !== "RVPN Drawal" && (
              <FilterMenu
                title="Type"
                items={TYPE}
                selectedItems={selectedSuffix.map((item) => item.displayName)}
                setSelectedItems={handleTypeChange}
              />
            )}
            <FilterMenu
              title="Chart Type"
              items={R_CHART_TYPES.map((item) => item.label)}
              selectedItems={[selectedChartType.label]}
              onSelect={(item, index) =>
                setSelectedChartType(R_CHART_TYPES[index])
              }
            />
          </div>
        </NewFilterContainer>

        <MainPageContainer>
          <SecondaryHeader />
          {metrics && (
            <div className="flex flex-wrap gap-4 my-0 justify-center">
              <MetricChip
                icon={<PeakIcon width={20} height={19} />}
                items={[
                  {
                    label: "Peak Load",
                    value: metrics["Peak Load (MW)"].value,
                    unit: metrics["Peak Load (MW)"].unit,
                  },
                ]}
              />
              <MetricChip
                icon={<AvgIcon width={20} height={19} />}
                items={[
                  {
                    label: "Average Load",
                    value: metrics["Average Load (MW)"].value,
                    unit: metrics["Average Load (MW)"].unit,
                  },
                ]}
              />
              <MetricChip
                icon={<MinIcon width={20} height={19} />}
                items={[
                  {
                    label: "Minimum Load",
                    value: metrics["Minimum Load (MW)"].value,
                    unit: metrics["Minimum Load (MW)"].unit,
                  },
                ]}
              />
              <MetricChip
                icon={<TotalIcon width={20} height={19} />}
                items={[
                  {
                    label: "Total Energy",
                    value: metrics["Total Energy (MU)"].value,
                    unit: metrics["Total Energy (MU)"].unit,
                  },
                ]}
              />
            </div>
          )}
          <div className="gas-chart-container">
            {loading ? (
              <ChartAreaLoader
                title="Loading Discom Load"
                subtitle="Fetching Demo State discom drawal data…"
              />
            ) : (
              <>
                <div className="gas-heading gap-x-10">
                  <h2 className="chartHeading w-60">
                    {selectedGroupKeys} ({selectedUnit})
                  </h2>
                  <div className="flex gap-x-6 justify-end">
                    {/* ▶ UPDATED — use uniqueBlock (from original) */}
                    <BlockRangeSelector
                      blocks={uniqueBlock}
                      placeholder="Search Block"
                      selectedOptions={selectedBlock.map((item) => item.value)}
                      setSelectedOptions={setSelectedBlock}
                    />
                    <button
                      onClick={() => {
                        dataTab.label === "Chart" &&
                          downloadChartTableCSV(tablePar);
                        dataTab.label === "Summary" &&
                          downloadSummaryCSV(tablePar);
                        dataTab.label === "Table" &&
                          downloadChartTableCSV(tablePar);
                      }}
                      className="downloadButton"
                    >
                      Download Data
                    </button>
                  </div>
                </div>

                {dataTab.label === "Chart" && (
                  <ReMixChart
                    key={`${selectedGroupKeys}-${selectedUnit}-${selectedInterval.key}-${selectedChartType.value}`}
                    data={chartData}
                    showBrush={chartData.length > 96 ? true : undefined}
                    brushStart={BrushStart.Start}
                    isTimeSlot={selectedInterval.isTime}
                    onlyTitle={true}
                    barCategoryGap={10}
                    unit={selectedUnit}
                    yAxisLabel={selectedUnit}
                    legends={legends}
                    onLegendSelect={handleLegendSelect}
                    defaultSelectedLegends={selectedLegends}
                    xDataKey={xDataKey}
                    xTick={
                      selectedInterval.isTime && selectedBlock.length === 0
                        ? (value: any, index: any) => renderHourTick(value, 10)
                        : undefined
                    }
                    secondXDataKey={secondDataKey()}
                  />
                )}

                {dataTab.label === "Summary" && (
                  <LegendSummaryTable {...tablePar} />
                )}
                {dataTab.label === "Table" && <ChartTable {...tablePar} />}
              </>
            )}
          </div>

          {/* Demo footer — 2-col layout, no Source tooltip */}
          <div className="gna-footer-container">
            <div className="grid grid-cols-2 justify-between gap-5">
              <p className="gna-footer-text text-start col-span-1">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Demo Version
              </strong>
            </div>
          </div>
        </MainPageContainer>
      </div>
    </>
  );
}
